import { useState } from 'react';
import { useForm } from 'react-hook-form';
import Modal from './ui/Modal';
import Button from './ui/Button';
import { createSponsor } from '../services/sponsorService';
import toast from 'react-hot-toast';

const AddSponsorModal = ({ isOpen, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const { register, handleSubmit, formState: { errors }, reset } = useForm();

  const onSubmit = async (data) => {
    setLoading(true);
    
    try {
      const sponsorData = {
        name: data.name,
        description: data.description,
        imageUrl: data.imageUrl,
        url: data.url || null,
        type: data.type,
        barbershopId: data.type === 'barbershop' ? data.barbershopId : null,
        priority: parseInt(data.priority),
        expiresAt: data.expiresAt,
        isActive: true,
        impressions: 0,
        clicks: 0
      };

      const result = await createSponsor(sponsorData);
      
      if (result.success) {
        toast.success('Patrocínio criado com sucesso!');
        reset();
        onSuccess();
        onClose();
      } else {
        toast.error(result.error || 'Erro ao criar patrocínio');
      }
    } catch (error) {
      toast.error('Erro inesperado ao criar patrocínio');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Adicionar Patrocínio">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Nome */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nome do Patrocinador
          </label>
          <input
            {...register('name', { required: 'Nome é obrigatório' })}
            type="text"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Ex: Barbearia Premium"
          />
          {errors.name && (
            <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
          )}
        </div>

        {/* Descrição */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Descrição
          </label>
          <textarea
            {...register('description')}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Descrição do patrocínio (opcional)"
            rows="2"
          />
        </div>

        {/* URL da Imagem */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            URL da Imagem do Banner *
          </label>
          <input
            {...register('imageUrl', { required: 'URL da imagem é obrigatória' })}
            type="url"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="https://exemplo.com/banner.jpg"
          />
          {errors.imageUrl && (
            <p className="text-red-500 text-sm mt-1">{errors.imageUrl.message}</p>
          )}
          <p className="text-xs text-gray-500 mt-1">
            Recomendado: 1200x400px
          </p>
        </div>

        {/* URL de Destino */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            URL de Destino (opcional)
          </label>
          <input
            {...register('url')}
            type="url"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="https://exemplo.com"
          />
          <p className="text-xs text-gray-500 mt-1">
            Link para onde o usuário será direcionado ao clicar
          </p>
        </div>

        {/* Tipo */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Tipo de Patrocínio
          </label>
          <select
            {...register('type', { required: 'Tipo é obrigatório' })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="banner">Banner Geral</option>
            <option value="barbershop">Barbearia Específica</option>
          </select>
        </div>

        {/* ID da Barbearia (condicional) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            ID da Barbearia (apenas para tipo "Barbearia Específica")
          </label>
          <input
            {...register('barbershopId')}
            type="text"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="ID da barbearia"
          />
        </div>

        {/* Prioridade */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Prioridade (1-10)
          </label>
          <input
            {...register('priority', { 
              required: 'Prioridade é obrigatória',
              min: { value: 1, message: 'Mínimo 1' },
              max: { value: 10, message: 'Máximo 10' }
            })}
            type="number"
            min="1"
            max="10"
            defaultValue="5"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {errors.priority && (
            <p className="text-red-500 text-sm mt-1">{errors.priority.message}</p>
          )}
          <p className="text-xs text-gray-500 mt-1">
            Maior prioridade = maior chance de aparecer
          </p>
        </div>

        {/* Data de Expiração */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Data de Expiração *
          </label>
          <input
            {...register('expiresAt', { required: 'Data de expiração é obrigatória' })}
            type="date"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {errors.expiresAt && (
            <p className="text-red-500 text-sm mt-1">{errors.expiresAt.message}</p>
          )}
        </div>

        {/* Botões */}
        <div className="flex gap-3 justify-end mt-6">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button type="submit" disabled={loading}>
            {loading ? 'Criando...' : 'Criar Patrocínio'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default AddSponsorModal;
